/* eslint-disable @typescript-eslint/no-misused-promises */
"use client";

import { FormEvent, useEffect, useState } from "react";
import { validateEmail, validatePassword } from "@/lib/validators";

import { Button } from "@/components/ui/button";
import { CardWrapper } from "@/components/card-wrapper";
import { FlagsUserInfoStrings } from "@/lib/userInfo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInputComponent } from "@/components/ui/password-input";
import { RequestSource } from "@/lib/errorHandling";
import { SocialAuth } from "@/components/social-auth";
import { toast } from "sonner";
import { useHandleError } from "@/hooks/useHandleError";
import { useNavigate } from "react-router";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";

export function LoginPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { handleError } = useHandleError();
  const { login } = usePocketBase();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(
      email !== "" &&
        password !== "" &&
        emailError === "" &&
        passwordError === "",
    );
  }, [email, password, emailError, passwordError]);

  const handleEmailLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateEmail(email, setEmailError);
    validatePassword(password, setPasswordError);
    if (!isFormValid) {
      return;
    }
    try {
      const userResponse = await login(email, password);
      const userFlags =
        userResponse.record.flags?.map((flag) => flag.key) || [];

      if (FlagsUserInfoStrings.every((flag) => userFlags.includes(flag))) {
        // If user has completed all steps, navigate to main app
        navigate("/app/voicechat");
      } else {
        navigate("/app/user-info");
      }
    } catch (error) {
      toast.error(handleError(RequestSource.LOGIN, error));
    }
  };

  const handleSocialLogin = (provider: string) => {
    // Handle social login logic here
    console.log(`${provider} login initiated`);
  };

  return (
    <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
      <CardWrapper className="w-[380px] md:w-[450px]">
        <div className="space-y-6 p-4 px-8">
          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-bold">
              {t("pages:loginSignup.loginTitle")}
            </h1>
            <p className="hidden md:inline text-gray-500 dark:text-gray-400">
              {t("pages:loginSignup.loginSubtitle")}
            </p>
          </div>
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("common:email")}</Label>
              <Input
                id="email"
                placeholder="m@example.com"
                required
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  validateEmail(e.target.value, setEmailError);
                }}
                className={emailError ? "border-red-500" : ""}
              />
              {emailError && (
                <p className="text-sm text-red-500">{emailError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("common:password")}</Label>
              <PasswordInputComponent
                id="password"
                required
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  validatePassword(e.target.value, setPasswordError);
                }}
                className={passwordError ? "border-red-500" : ""}
              />
              {passwordError && (
                <p className="text-sm text-red-500">{passwordError}</p>
              )}
            </div>
            <Button className="w-full" type="submit" disabled={!isFormValid}>
              {t("pages:loginSignup.loginButton")}
            </Button>
          </form>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              {/* <span className="bg-background px-2 text-muted-foreground">
                {t("pages:loginSignup.orContinueWith")}
              </span> */}
            </div>
          </div>
          {/* <div className="flex-grow">
            <SocialAuth handleSocialAuth={handleSocialLogin} />
          </div> */}
          <div className="text-center text-sm">
            <a className="underline" href="/password-recovery">
              {t("pages:loginSignup.forgotPassword")}
            </a>
          </div>
          <p className="mt-2 text-sm text-center text-muted-foreground">
            {t("pages:loginSignup.notMember")}{" "}
            <a
              href="/signup"
              className="font-medium text-primary hover:text-primary/90"
            >
              {t("pages:loginSignup.createAccount")}
            </a>
          </p>
        </div>
      </CardWrapper>
    </div>
  );
}
